import { useApi } from '@gravitate-js/the-armory'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { getAPIURL } from '@utils/api'

// Types for product movements
interface BaseProductMovementCommon {
  product_name: string
  date: string
  action: 'load' | 'drop'
}

export interface LoadProductMovement extends BaseProductMovementCommon {
  action: 'load'
  supplier_name: string
  bol_number: string
  net_volume: number
  gross_volume: number
}

export interface DropProductMovement extends BaseProductMovementCommon {
  action: 'drop'
  tank_id: string
  dropped_volume: number
  before_measurement: number
  after_measurement: number
  measurement_units: string
  lid_code_url: string
}

export type BaseProductMovement = LoadProductMovement | DropProductMovement

// Types for route locations
export interface RouteLocation {
  location_name: string
  lat: number
  lon: number
  site_id?: string
  action: 'load' | 'drop'
  departed_for: string | null
  arrived_at: string | null
  action_started_at: string | null
  action_completed_at: string | null
  distance_miles: number
  product_movements: Array<DropProductMovement | LoadProductMovement>
  travel_time_seconds: number | null
  // Optional description fields that may be used for UI display
  departed_for_description?: string
  arrived_at_description?: string
  action_started_at_description?: string
  action_completed_at_description?: string
}

// Type for route log response
export interface RouteLogResponse {
  route: RouteLocation[]
  trailer_info?: {
    trailer_config?: string
    compartments?: Array<{
      compartment_index?: number
      product_name?: string
      product_id?: string
      lid_code_url?: string
    }>
  }
}

export const useOrderRouteLog = () => {
  const api = useApi({ baseURLOverride: getAPIURL() })

  const useOrderRouteLogQuery = (orderId?: string): UseQueryResult<RouteLogResponse> => {
    return useQuery({
      queryKey: ['order-route-log', orderId],
      queryFn: async () => {
        const response = await api.post('order/route_log', undefined, {
          queryParams: new URLSearchParams({ order_id: orderId as string }),
        })
        return response
      },
      enabled: !!orderId,
      refetchOnWindowFocus: false,
    })
  }

  return {
    useOrderRouteLogQuery,
  }
}
